import type { Theme } from "@mui/material";
import { Box, Dialog, DialogContent, DialogTitle, Popover, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import useIsUserActive from "../../../hooks/useIsUserActive";
import { usePageOrientation } from "../../../hooks/usePageOrientation";
import PdfDocumentWrapper from "./PdfDocumentWrapper";
import PdfPagesContainer from "./PdfPagesContainer";
import PdfThumbnailsContainer from "./PdfThumbnailsContainer";
import PdfViewerControls from "./PdfViewerControls";
import { usePdfViewer } from "./pdfViewerState";
import { pdfViewerStyles } from "./pdfViewerStyles";
import usePageViewTracking from "./usePageViewTracking";

interface Props {
  open: boolean;
  onClose: () => void;
  fileName: string;
  fileSrc: string;
  documentViewSessionId?: string;
  preventDownload?: boolean;
  onPdfLoadError?: (error: Error, fileSrc: string) => void;
}

const PdfViewerDialog = ({
  open,
  onClose,
  fileName,
  fileSrc,
  documentViewSessionId,
  preventDownload,
  onPdfLoadError,
}: Props) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const pageOrientation = usePageOrientation();

  const {
    viewerState,
    containerEl,
    setContainerEl,
    pageRefs,
    containerSize,
    handleScroll,
    handleKeyDown,
    navigateToPage,
    toggleThumbnails,
    handleDocumentLoaded,
    updateLoadingProgress,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    resetState,
  } = usePdfViewer();

  const isUserActive = useIsUserActive();

  usePageViewTracking({
    sessionId: documentViewSessionId,
    numPages: viewerState.numPages,
    visiblePages: viewerState.currentPageNumber > 0 ? [viewerState.currentPageNumber] : [],
    pauseTracking: !isUserActive,
  });

  useEffect(() => {
    const onGestureStart = (e: Event) => {
      e.preventDefault(); // disable pinch zoom
    };

    document.addEventListener("gesturestart", onGestureStart, { passive: false });

    return () => {
      document.removeEventListener("gesturestart", onGestureStart);
    };
  });

  const [dialogTitleEl, setDialogTitleEl] = useState<HTMLDivElement | null>(null);

  const pageHeight = useMemo(
    () => (pageOrientation === "landscape" && containerSize?.height ? containerSize.height - 16 : undefined),
    [containerSize, pageOrientation]
  );

  const pageWidth = useMemo(
    () => (pageOrientation === "portrait" && containerSize?.width ? containerSize.width - 16 : undefined),
    [containerSize, pageOrientation]
  );

  const handleClose = () => {
    onClose();
    setTimeout(() => resetState());
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      fullWidth
      maxWidth="xl"
      onKeyDown={handleKeyDown}
      sx={isSmallScreen ? undefined : { ".MuiPaper-root": { maxHeight: "100vh", height: "100vh" } }}
    >
      <DialogTitle
        component="div"
        ref={setDialogTitleEl}
        sx={(t) => ({ py: 1, px: isSmallScreen ? 1 : 2.5, borderBottom: `1px solid ${t.palette.divider}` })}
      >
        <PdfViewerControls
          fileName={fileName}
          viewerState={viewerState}
          documentViewSessionId={documentViewSessionId}
          hideFileName={pageOrientation === "portrait"}
          hideDownloadButton={preventDownload}
          hidePrintButton={preventDownload || pageOrientation === "portrait"}
          onNavigateToPage={navigateToPage}
          onToggleThumbnails={toggleThumbnails}
          onZoomOut={zoomOut}
          onZoomIn={zoomIn}
          onRotateLeft={rotateLeft}
          onRotateRight={rotateRight}
          onClose={handleClose}
        />
      </DialogTitle>
      <DialogContent ref={setContainerEl} onScroll={handleScroll} sx={{ px: 0, bgcolor: pdfViewerStyles.bgcolor }}>
        <Box onContextMenu={(e) => e.preventDefault()} sx={{ height: "100%" }}>
          <PdfDocumentWrapper
            fileSrc={fileSrc}
            viewerState={viewerState}
            onDocumentLoad={handleDocumentLoaded}
            onLoadingProgress={updateLoadingProgress}
            onPdfLoadError={onPdfLoadError}
            preventDownload={preventDownload}
          >
            <Popover
              open={viewerState.showThumbnails}
              onClose={() => toggleThumbnails()}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              anchorEl={dialogTitleEl}
              sx={{ "& .MuiPopover-paper": { maxHeight: containerSize?.height } }}
            >
              <PdfThumbnailsContainer viewerState={viewerState} />
            </Popover>

            <PdfPagesContainer
              viewerState={viewerState}
              containerEl={containerEl}
              pageRefs={pageRefs}
              pageHeight={pageHeight}
              pageWidth={pageWidth}
            />
          </PdfDocumentWrapper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerDialog;
